import React, { useState } from 'react';
import { Flame, ChevronLeft, ChevronRight, Trophy, Award, Users, Home, RefreshCw, Share, Bookmark, BookOpen } from 'lucide-react';
import { BrowserFooter, Vis_Header, Vis_Container } from './visComponents';
import { getRandomNumberBetween } from './visUtils';
import { CURRENT_USER_TYPE } from './visComponents';

const USER_TYPES = { 
  high: {
    consistency: getRandomNumberBetween(80, 99),
    rank: getRandomNumberBetween(1, 100),
    totalDays: getRandomNumberBetween(20, 30),
    streakDays: getRandomNumberBetween(10, 30),
    previousMonthPercentage: getRandomNumberBetween(80, 99)
  },
  medium: {
    consistency: getRandomNumberBetween(34, 74),
    rank: getRandomNumberBetween(10000, 20000),
    totalDays: getRandomNumberBetween(10, 15),
    streakDays: getRandomNumberBetween(3, 8),
    previousMonthPercentage: getRandomNumberBetween(34, 74)
  },
  low: {
    consistency: getRandomNumberBetween(0, 33),
    rank: getRandomNumberBetween(30000, 40000),
    totalDays: getRandomNumberBetween(1, 10),
    streakDays: getRandomNumberBetween(0, 3),
    previousMonthPercentage: getRandomNumberBetween(0, 33)
  }
}


//////////////// UPDATE THIS  ////////////////
const EFFECTIVE_USER_TYPE = USER_TYPES[CURRENT_USER_TYPE]

const CONSISTENCY = EFFECTIVE_USER_TYPE.consistency
const RANK = EFFECTIVE_USER_TYPE.rank
const TOTAL_DAYS = EFFECTIVE_USER_TYPE.totalDays
const STREAK_DAYS = EFFECTIVE_USER_TYPE.streakDays
const PREVIOUS_MONTH_PERCENTAGE = EFFECTIVE_USER_TYPE.previousMonthPercentage

const VisualScreen1 = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  const generateCalendarData = () => {
    const today = new Date();
    const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
    const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
    
    const calendarDays = [];
    
    // Add empty cells for days before the 1st of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      calendarDays.push({ day: null, studied: false });
    }
    
    // Check if we're viewing the current month
    const isCurrentMonth = currentMonth.getMonth() === today.getMonth() && 
                           currentMonth.getFullYear() === today.getFullYear();
    
    // Check if we're viewing the previous month
    const isPreviousMonth = 
      (currentMonth.getMonth() === today.getMonth() - 1 && currentMonth.getFullYear() === today.getFullYear()) ||
      (today.getMonth() === 0 && currentMonth.getMonth() === 11 && currentMonth.getFullYear() === today.getFullYear() - 1);
    
    // Fill in the calendar
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const isInPast = date <= today;
      let studied = false;
      
      if (isCurrentMonth && isInPast) {
        // For current month, use the TOTAL_DAYS and STREAK_DAYS variables
        const dayOfMonth = day;
        const todayDate = today.getDate();
        
        // If this day is part of the current streak (last STREAK_DAYS days up to today)
        if (dayOfMonth > todayDate - STREAK_DAYS && dayOfMonth <= todayDate) {
          studied = true;
        } 
        // Day before streak should not be studied
        else if (dayOfMonth === todayDate - STREAK_DAYS) {
          studied = false;
        }
        // Distribute remaining studied days to reach TOTAL_DAYS
        else if (dayOfMonth <= todayDate) {
          // Calculate how many more studied days we need
          const remainingStudiedDays = TOTAL_DAYS - STREAK_DAYS;
          const daysBeforeStreak = todayDate - STREAK_DAYS - 1;
          
          // Probability to make a day studied to reach exactly TOTAL_DAYS
          if (daysBeforeStreak > 0) {
            studied = Math.random() < (remainingStudiedDays / daysBeforeStreak);
          }
        }
      } else if (isPreviousMonth && isInPast) {
        // For previous month, use PREVIOUS_MONTH_PERCENTAGE
        studied = Math.random() < (PREVIOUS_MONTH_PERCENTAGE / 100);
      } else if (isInPast) {
        // For other past months, just use random data with lower percentage
        studied = Math.random() < 0.7; // 70% chance of having studied
      }
      
      calendarDays.push({
        day,
        date: new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day),
        studied
      });
    }
    
    // Ensure exactly TOTAL_DAYS are studied in the current month
    if (isCurrentMonth) {
      const pastDays = calendarDays.filter(day => day.day !== null && day.date <= today);
      const studiedDays = pastDays.filter(day => day.studied).length;
      
      if (studiedDays < TOTAL_DAYS) {
        // Need to add more studied days
        const nonStudiedDays = pastDays.filter(day => !day.studied && 
          // Don't touch the day before streak
          day.date.getDate() !== today.getDate() - STREAK_DAYS);
        
        // Randomly select days to mark as studied
        for (let i = 0; i < TOTAL_DAYS - studiedDays && i < nonStudiedDays.length; i++) {
          const randomIndex = Math.floor(Math.random() * nonStudiedDays.length);
          const dayToChange = nonStudiedDays.splice(randomIndex, 1)[0];
          
          // Find this day in the original array and mark it as studied
          const originalIndex = calendarDays.findIndex(d => 
            d.day === dayToChange.day && d.date && dayToChange.date && 
            d.date.getTime() === dayToChange.date.getTime());
          
          if (originalIndex !== -1) {
            calendarDays[originalIndex].studied = true;
          }
        }
      } else if (studiedDays > TOTAL_DAYS) {
        // Need to remove some studied days
        const studiedNonStreakDays = pastDays.filter(day => 
          day.studied && 
          (day.date.getDate() <= today.getDate() - STREAK_DAYS || 
           day.date.getDate() > today.getDate()));
        
        // Randomly select days to mark as not studied
        for (let i = 0; i < studiedDays - TOTAL_DAYS && i < studiedNonStreakDays.length; i++) {
          const randomIndex = Math.floor(Math.random() * studiedNonStreakDays.length);
          const dayToChange = studiedNonStreakDays.splice(randomIndex, 1)[0];
          
          // Find this day in the original array and mark it as not studied
          const originalIndex = calendarDays.findIndex(d => 
            d.day === dayToChange.day && d.date && dayToChange.date && 
            d.date.getTime() === dayToChange.date.getTime());
          
          if (originalIndex !== -1) {
            calendarDays[originalIndex].studied = false;
          }
        }
      }
    }
    
    // Ensure previous month has exactly PREVIOUS_MONTH_PERCENTAGE of days studied
    if (isPreviousMonth) {
      const pastDays = calendarDays.filter(day => day.day !== null && day.date <= today);
      const totalPastDays = pastDays.length;
      const targetStudiedDays = Math.round((PREVIOUS_MONTH_PERCENTAGE / 100) * totalPastDays);
      const studiedDays = pastDays.filter(day => day.studied).length;
      
      if (studiedDays < targetStudiedDays) {
        // Need to add more studied days
        const nonStudiedDays = pastDays.filter(day => !day.studied);
        
        // Randomly select days to mark as studied
        for (let i = 0; i < targetStudiedDays - studiedDays && i < nonStudiedDays.length; i++) {
          const randomIndex = Math.floor(Math.random() * nonStudiedDays.length);
          const dayToChange = nonStudiedDays.splice(randomIndex, 1)[0];
          
          // Find this day in the original array and mark it as studied
          const originalIndex = calendarDays.findIndex(d => 
            d.day === dayToChange.day && d.date && dayToChange.date && 
            d.date.getTime() === dayToChange.date.getTime());
          
          if (originalIndex !== -1) {
            calendarDays[originalIndex].studied = true;
          }
        }
      } else if (studiedDays > targetStudiedDays) {
        // Need to remove some studied days
        const studiedDays = pastDays.filter(day => day.studied);
        
        // Randomly select days to mark as not studied
        for (let i = 0; i < studiedDays.length - targetStudiedDays; i++) {
          const randomIndex = Math.floor(Math.random() * studiedDays.length);
          const dayToChange = studiedDays.splice(randomIndex, 1)[0];
          
          // Find this day in the original array and mark it as not studied
          const originalIndex = calendarDays.findIndex(d => 
            d.day === dayToChange.day && d.date && dayToChange.date && 
            d.date.getTime() === dayToChange.date.getTime());
          
          if (originalIndex !== -1) {
            calendarDays[originalIndex].studied = false;
          }
        }
      }
    }
    
    return calendarDays;
  };
  const calendarData = generateCalendarData();
  
  const getStudyColor = (studied) => {
    return studied ? 'rgb(99, 102, 241)' : 'rgb(243, 244, 246)';
  };
  
  const handlePrevMonth = () => {
    const prevMonth = new Date(currentMonth);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    setCurrentMonth(prevMonth);
  };
  
  const handleNextMonth = () => {
    const nextMonth = new Date(currentMonth);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    setCurrentMonth(nextMonth);
  };
  
  // Day of week headers
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  return (

    <Vis_Container>

      <Vis_Header title="Study Streak" />
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', padding: '1.5rem', gap: '1rem', background: 'rgb(238, 242, 255)', }}>
        {[
          { icon: Flame, color: 'rgb(249, 115, 22)', value: STREAK_DAYS, label: 'Current Streak' },
          { icon: Trophy, color: 'rgb(234, 179, 8)', value: `${CONSISTENCY}%`, label: 'Consistency' },
          { icon: Award, color: 'rgb(99, 102, 241)', value: `#${RANK}`, label: 'Rank (of 43,516)' }
        ].map((metric, index) => (
          <div key={index} style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
              <metric.icon style={{ color: metric.color }} size={36} strokeWidth={1.5} />
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'rgb(31, 41, 55)', marginBottom: '0.5rem' }}>{metric.value}</div>
            <div style={{ fontSize: '1rem', color: 'rgb(107, 114, 128)' }}>{metric.label}</div>
          </div>
        ))}
      </div>
      
      {/* Calendar */}
      <div style={{ paddingTop: '1.5rem', paddingLeft: '1.5rem', paddingRight: '1.5rem', }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ fontWeight: '600', color: 'rgb(31, 41, 55)' }}>
            {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h3>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button 
              onClick={handlePrevMonth}
              style={{ padding: '0.25rem', borderRadius: '9999px' }}
              onMouseOver={(e) => e.currentTarget.style.background = 'rgb(243, 244, 246)'}
              onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <ChevronLeft size={18} />
            </button>
            <button 
              onClick={handleNextMonth}
              style={{ padding: '0.25rem', borderRadius: '9999px' }}
              onMouseOver={(e) => e.currentTarget.style.background = 'rgb(243, 244, 246)'}
              onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
        
        {/* Calendar grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0.25rem' }}>
          {/* Weekday headers */}
          {weekdays.map((day, i) => (
            <div key={i} style={{ fontSize: '0.75rem', textAlign: 'center', fontWeight: '500', color: 'rgb(107, 114, 128)', padding: '0.25rem 0' }}>
              {day}
            </div>
          ))}
          
          {/* Calendar days */}
          {calendarData.map((day, i) => (
            <div style={{ position: 'relative' }}
              key={i}
            >
              <div style={{ aspectRatio: '1/1', borderRadius: '0.375rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', background: day.day ? (day.studied ? getStudyColor(true) : getStudyColor(false)) : 'transparent', color: day.studied ? 'white' : 'rgb(107, 114, 128)' }}>
                {day.day}
              </div>
            </div>
          ))}
        </div>
        
      

        {/* Legend */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginRight: '1rem' }}>
            <div style={{ width: '1rem', height: '1rem', backgroundColor: getStudyColor(true), borderRadius: '0.25rem', marginRight: '0.5rem' }}></div>
            <span style={{ fontSize: '0.75rem', color: 'rgb(107, 114, 128)' }}>Days Studied</span>
          </div>
        </div>

      </div>
      
      <BrowserFooter />
    </Vis_Container>
  );
};

export default VisualScreen1;
